/**
 * Comprehensive location database using browser-compatible sources
 * Uses country-state-city plus curated major cities data
 */

import { Country, State, City } from 'country-state-city'

export interface LocationItem {
  id: string
  name: string
  country: string
  countryCode: string
  state?: string
  stateCode?: string
  type: 'city' | 'country' | 'state'
  population?: number
  searchTerms: string[]
  displayName: string
  latitude?: number
  longitude?: number
}

class LocationDatabase {
  private locations: LocationItem[] = []
  private initialized = false
  
  get isInitialized() {
    return this.initialized
  }

  async initialize() {
    if (this.initialized) return
    
    console.log('Initializing location database...')
    
    try {
      // Yield to allow UI to remain responsive
      await new Promise(resolve => setTimeout(resolve, 0))
      
      // Get all countries
      const countries = Country.getAllCountries()
      console.log(`Loaded ${countries.length} countries`)
      
      // Add curated major cities with population data - comprehensive worldwide coverage
      const majorCitiesData = [
        // North America - United States
        { name: 'New York', country: 'US', population: 8175133, lat: 40.7128, lng: -74.0060 },
        { name: 'Los Angeles', country: 'US', population: 3792621, lat: 34.0522, lng: -118.2437 },
        { name: 'Chicago', country: 'US', population: 2695598, lat: 41.8781, lng: -87.6298 },
        { name: 'Houston', country: 'US', population: 2320268, lat: 29.7604, lng: -95.3698 },
        { name: 'Phoenix', country: 'US', population: 1680992, lat: 33.4484, lng: -112.0740 },
        { name: 'Philadelphia', country: 'US', population: 1584064, lat: 39.9526, lng: -75.1652 },
        { name: 'San Antonio', country: 'US', population: 1547253, lat: 29.4241, lng: -98.4936 },
        { name: 'San Diego', country: 'US', population: 1423851, lat: 32.7157, lng: -117.1611 },
        { name: 'Dallas', country: 'US', population: 1343573, lat: 32.7767, lng: -96.7970 },
        { name: 'San Jose', country: 'US', population: 1021795, lat: 37.3382, lng: -121.8863 },
        { name: 'Austin', country: 'US', population: 978908, lat: 30.2672, lng: -97.7431 },
        { name: 'Jacksonville', country: 'US', population: 911507, lat: 30.3322, lng: -81.6557 },
        { name: 'Fort Worth', country: 'US', population: 909585, lat: 32.7555, lng: -97.3308 },
        { name: 'Columbus', country: 'US', population: 898553, lat: 39.9612, lng: -82.9988 },
        { name: 'San Francisco', country: 'US', population: 873965, lat: 37.7749, lng: -122.4194 },
        { name: 'Charlotte', country: 'US', population: 885708, lat: 35.2271, lng: -80.8431 },
        { name: 'Indianapolis', country: 'US', population: 876384, lat: 39.7684, lng: -86.1581 },
        { name: 'Seattle', country: 'US', population: 753675, lat: 47.6062, lng: -122.3321 },
        { name: 'Denver', country: 'US', population: 715522, lat: 39.7392, lng: -104.9903 },
        { name: 'Washington', country: 'US', population: 705749, lat: 38.9072, lng: -77.0369 },
        { name: 'Boston', country: 'US', population: 685094, lat: 42.3601, lng: -71.0589 },
        { name: 'Las Vegas', country: 'US', population: 651319, lat: 36.1699, lng: -115.1398 },
        { name: 'Miami', country: 'US', population: 467963, lat: 25.7617, lng: -80.1918 },
        { name: 'Atlanta', country: 'US', population: 498715, lat: 33.7490, lng: -84.3880 },
        { name: 'Nashville', country: 'US', population: 689447, lat: 36.1627, lng: -86.7816 },
        { name: 'Portland', country: 'US', population: 652503, lat: 45.5152, lng: -122.6784 },
        { name: 'New Orleans', country: 'US', population: 390144, lat: 29.9511, lng: -90.0715 },
        { name: 'Newark', country: 'US', population: 311549, lat: 40.7357, lng: -74.1724 },
        { name: 'Detroit', country: 'US', population: 670031, lat: 42.3314, lng: -83.0458 },
        { name: 'Minneapolis', country: 'US', population: 429954, lat: 44.9778, lng: -93.2650 },
        
        // North America - Canada
        { name: 'Toronto', country: 'CA', population: 2731571, lat: 43.6532, lng: -79.3832 },
        { name: 'Montreal', country: 'CA', population: 1704694, lat: 45.5017, lng: -73.5673 },
        { name: 'Vancouver', country: 'CA', population: 631486, lat: 49.2827, lng: -123.1207 },
        { name: 'Calgary', country: 'CA', population: 1239220, lat: 51.0447, lng: -114.0719 },
        { name: 'Edmonton', country: 'CA', population: 972223, lat: 53.5461, lng: -113.4938 },
        { name: 'Ottawa', country: 'CA', population: 994837, lat: 45.4215, lng: -75.6972 },
        { name: 'Winnipeg', country: 'CA', population: 749534, lat: 49.8951, lng: -97.1384 },
        { name: 'Quebec City', country: 'CA', population: 531902, lat: 46.8139, lng: -71.2080 },
        { name: 'Hamilton', country: 'CA', population: 536917, lat: 43.2557, lng: -79.8711 },
        
        // North America - Mexico
        { name: 'Mexico City', country: 'MX', population: 21804515, lat: 19.4326, lng: -99.1332 },
        { name: 'Guadalajara', country: 'MX', population: 1385629, lat: 20.6597, lng: -103.3496 },
        { name: 'Monterrey', country: 'MX', population: 1135512, lat: 25.6866, lng: -100.3161 },
        { name: 'Puebla', country: 'MX', population: 1434062, lat: 19.0414, lng: -98.2063 },
        { name: 'Tijuana', country: 'MX', population: 1641570, lat: 32.5149, lng: -117.0382 },
        { name: 'León', country: 'MX', population: 1238962, lat: 21.1619, lng: -101.6921 },
        { name: 'Juárez', country: 'MX', population: 1321004, lat: 31.6904, lng: -106.4245 },
        { name: 'Zapopan', country: 'MX', population: 1142483, lat: 20.7214, lng: -103.3918 },
        { name: 'Cancún', country: 'MX', population: 628306, lat: 21.1619, lng: -86.8515 },
        
        // Caribbean & Central America
        { name: 'Santo Domingo', country: 'DO', population: 2201941, lat: 18.4861, lng: -69.9312 },
        { name: 'Santiago de los Caballeros', country: 'DO', population: 771748, lat: 19.4517, lng: -70.6970 },
        { name: 'San Pedro de Macorís', country: 'DO', population: 217899, lat: 18.4539, lng: -69.2975 },
        { name: 'La Romana', country: 'DO', population: 130426, lat: 18.4273, lng: -68.9728 },
        { name: 'Punta Cana', country: 'DO', population: 100023, lat: 18.5601, lng: -68.3725 },
        { name: 'Havana', country: 'CU', population: 2106146, lat: 23.1136, lng: -82.3666 },
        { name: 'Kingston', country: 'JM', population: 937700, lat: 17.9970, lng: -76.7936 },
        { name: 'Port-au-Prince', country: 'HT', population: 2637297, lat: 18.5944, lng: -72.3074 },
        { name: 'San José', country: 'CR', population: 333980, lat: 9.9281, lng: -84.0907 },
        { name: 'Panama City', country: 'PA', population: 880691, lat: 8.9824, lng: -79.5199 },
        { name: 'Guatemala City', country: 'GT', population: 994938, lat: 14.6349, lng: -90.5069 },
        { name: 'San Salvador', country: 'SV', population: 567698, lat: 13.6929, lng: -89.2182 },
        { name: 'Tegucigalpa', country: 'HN', population: 1157509, lat: 14.0723, lng: -87.1921 },
        { name: 'Managua', country: 'NI', population: 1028808, lat: 12.1364, lng: -86.2514 },
        
        // South America
        { name: 'São Paulo', country: 'BR', population: 22430000, lat: -23.5558, lng: -46.6396 },
        { name: 'Rio de Janeiro', country: 'BR', population: 13458000, lat: -22.9068, lng: -43.1729 },
        { name: 'Brasília', country: 'BR', population: 3055149, lat: -15.8267, lng: -47.9218 },
        { name: 'Salvador', country: 'BR', population: 2886698, lat: -12.9714, lng: -38.5014 },
        { name: 'Fortaleza', country: 'BR', population: 2669342, lat: -3.7319, lng: -38.5267 },
        { name: 'Belo Horizonte', country: 'BR', population: 2521564, lat: -19.9167, lng: -43.9345 },
        { name: 'Manaus', country: 'BR', population: 2182763, lat: -3.1190, lng: -60.0217 },
        { name: 'Curitiba', country: 'BR', population: 1948626, lat: -25.4244, lng: -49.2654 },
        { name: 'Recife', country: 'BR', population: 1653461, lat: -8.0476, lng: -34.8770 },
        { name: 'Porto Alegre', country: 'BR', population: 1488252, lat: -30.0346, lng: -51.2177 },
        { name: 'Buenos Aires', country: 'AR', population: 15370000, lat: -34.6118, lng: -58.3960 },
        { name: 'Córdoba', country: 'AR', population: 1613000, lat: -31.4201, lng: -64.1888 },
        { name: 'Rosario', country: 'AR', population: 1198528, lat: -32.9442, lng: -60.6505 },
        { name: 'Mendoza', country: 'AR', population: 115021, lat: -32.8895, lng: -68.8458 },
        { name: 'Lima', country: 'PE', population: 10719000, lat: -12.0464, lng: -77.0428 },
        { name: 'Arequipa', country: 'PE', population: 1008290, lat: -16.4090, lng: -71.5375 },
        { name: 'Bogotá', country: 'CO', population: 7412566, lat: 4.7110, lng: -74.0721 },
        { name: 'Medellín', country: 'CO', population: 2508452, lat: 6.2442, lng: -75.5812 },
        { name: 'Cali', country: 'CO', population: 2252616, lat: 3.4516, lng: -76.5320 },
        { name: 'Barranquilla', country: 'CO', population: 1274250, lat: 10.9685, lng: -74.7813 },
        { name: 'Santiago', country: 'CL', population: 6680000, lat: -33.4489, lng: -70.6693 },
        { name: 'Valparaíso', country: 'CL', population: 296655, lat: -33.0458, lng: -71.6197 },
        { name: 'Caracas', country: 'VE', population: 2923959, lat: 10.4806, lng: -66.9036 },
        { name: 'Maracaibo', country: 'VE', population: 1653211, lat: 10.6666, lng: -71.6124 },
        { name: 'Quito', country: 'EC', population: 2781641, lat: -0.1807, lng: -78.4678 },
        { name: 'Guayaquil', country: 'EC', population: 2698077, lat: -2.1707, lng: -79.9218 },
        { name: 'La Paz', country: 'BO', population: 835361, lat: -16.4897, lng: -68.1193 },
        { name: 'Santa Cruz', country: 'BO', population: 1364389, lat: -17.8146, lng: -63.1560 },
        { name: 'Asunción', country: 'PY', population: 525294, lat: -25.2637, lng: -57.5759 },
        { name: 'Montevideo', country: 'UY', population: 1319108, lat: -34.9011, lng: -56.1645 },
        { name: 'Georgetown', country: 'GY', population: 235017, lat: 6.8013, lng: -58.1551 },
        { name: 'Paramaribo', country: 'SR', population: 241000, lat: 5.8520, lng: -55.2038 },
        
        // Europe - Western Europe
        { name: 'London', country: 'GB', population: 9648110, lat: 51.5074, lng: -0.1278 },
        { name: 'Birmingham', country: 'GB', population: 1153717, lat: 52.4862, lng: -1.8904 },
        { name: 'Manchester', country: 'GB', population: 547858, lat: 53.4808, lng: -2.2426 },
        { name: 'Glasgow', country: 'GB', population: 633120, lat: 55.8642, lng: -4.2518 },
        { name: 'Liverpool', country: 'GB', population: 498042, lat: 53.4084, lng: -2.9916 },
        { name: 'Edinburgh', country: 'GB', population: 518500, lat: 55.9533, lng: -3.1883 },
        { name: 'Paris', country: 'FR', population: 11027000, lat: 48.8566, lng: 2.3522 },
        { name: 'Marseille', country: 'FR', population: 873076, lat: 43.2965, lng: 5.3698 },
        { name: 'Lyon', country: 'FR', population: 518635, lat: 45.7640, lng: 4.8357 },
        { name: 'Toulouse', country: 'FR', population: 479553, lat: 43.6047, lng: 1.4442 },
        { name: 'Nice', country: 'FR', population: 348085, lat: 43.7102, lng: 7.2620 },
        { name: 'Nantes', country: 'FR', population: 320732, lat: 47.2184, lng: -1.5536 },
        { name: 'Berlin', country: 'DE', population: 3748148, lat: 52.5200, lng: 13.4050 },
        { name: 'Hamburg', country: 'DE', population: 1945532, lat: 53.5511, lng: 9.9937 },
        { name: 'Munich', country: 'DE', population: 1488202, lat: 48.1351, lng: 11.5820 },
        { name: 'Cologne', country: 'DE', population: 1073096, lat: 50.9375, lng: 6.9603 },
        { name: 'Frankfurt', country: 'DE', population: 758847, lat: 50.1109, lng: 8.6821 },
        { name: 'Stuttgart', country: 'DE', population: 626275, lat: 48.7758, lng: 9.1829 },
        { name: 'Düsseldorf', country: 'DE', population: 621877, lat: 51.2277, lng: 6.7735 },
        { name: 'Leipzig', country: 'DE', population: 597493, lat: 51.3397, lng: 12.3731 },
        { name: 'Madrid', country: 'ES', population: 6642000, lat: 40.4168, lng: -3.7038 },
        { name: 'Barcelona', country: 'ES', population: 5585556, lat: 41.3851, lng: 2.1734 },
        { name: 'Valencia', country: 'ES', population: 794288, lat: 39.4699, lng: -0.3763 },
        { name: 'Seville', country: 'ES', population: 688711, lat: 37.3886, lng: -5.9823 },
        { name: 'Zaragoza', country: 'ES', population: 674997, lat: 41.6488, lng: -0.8891 },
        { name: 'Málaga', country: 'ES', population: 578460, lat: 36.7213, lng: -4.4214 },
        { name: 'Rome', country: 'IT', population: 4342212, lat: 41.9028, lng: 12.4964 },
        { name: 'Milan', country: 'IT', population: 1396059, lat: 45.4642, lng: 9.1900 },
        { name: 'Naples', country: 'IT', population: 967068, lat: 40.8518, lng: 14.2681 },
        { name: 'Turin', country: 'IT', population: 870952, lat: 45.0703, lng: 7.6869 },
        { name: 'Palermo', country: 'IT', population: 673735, lat: 38.1157, lng: 13.3613 },
        { name: 'Genoa', country: 'IT', population: 583601, lat: 44.4056, lng: 8.9463 },
        { name: 'Amsterdam', country: 'NL', population: 1158364, lat: 52.3676, lng: 4.9041 },
        { name: 'Rotterdam', country: 'NL', population: 651376, lat: 51.9244, lng: 4.4777 },
        { name: 'The Hague', country: 'NL', population: 544766, lat: 52.0705, lng: 4.3007 },
        { name: 'Utrecht', country: 'NL', population: 361966, lat: 52.0907, lng: 5.1214 },
        { name: 'Brussels', country: 'BE', population: 1208542, lat: 50.8503, lng: 4.3517 },
        { name: 'Antwerp', country: 'BE', population: 529247, lat: 51.2194, lng: 4.4025 },
        { name: 'Ghent', country: 'BE', population: 262219, lat: 51.0500, lng: 3.7303 },
        { name: 'Zurich', country: 'CH', population: 415367, lat: 47.3769, lng: 8.5417 },
        { name: 'Geneva', country: 'CH', population: 201818, lat: 46.2044, lng: 6.1432 },
        { name: 'Basel', country: 'CH', population: 177595, lat: 47.5596, lng: 7.5886 },
        { name: 'Vienna', country: 'AT', population: 1975546, lat: 48.2082, lng: 16.3738 },
        { name: 'Graz', country: 'AT', population: 331562, lat: 47.0707, lng: 15.4395 },
        { name: 'Linz', country: 'AT', population: 206604, lat: 48.3059, lng: 14.2862 },
        { name: 'Lisbon', country: 'PT', population: 547733, lat: 38.7223, lng: -9.1393 },
        { name: 'Porto', country: 'PT', population: 231962, lat: 41.1579, lng: -8.6291 },
        { name: 'Dublin', country: 'IE', population: 1228179, lat: 53.3498, lng: -6.2603 },
        { name: 'Cork', country: 'IE', population: 208669, lat: 51.8985, lng: -8.4756 },
        
        // Europe - Northern Europe
        { name: 'Stockholm', country: 'SE', population: 1633000, lat: 59.3293, lng: 18.0686 },
        { name: 'Gothenburg', country: 'SE', population: 583056, lat: 57.7089, lng: 11.9746 },
        { name: 'Malmö', country: 'SE', population: 347949, lat: 55.6059, lng: 13.0007 },
        { name: 'Oslo', country: 'NO', population: 693494, lat: 59.9139, lng: 10.7522 },
        { name: 'Bergen', country: 'NO', population: 283929, lat: 60.3913, lng: 5.3221 },
        { name: 'Copenhagen', country: 'DK', population: 660193, lat: 55.6761, lng: 12.5683 },
        { name: 'Aarhus', country: 'DK', population: 285273, lat: 56.1629, lng: 10.2039 },
        { name: 'Helsinki', country: 'FI', population: 658864, lat: 60.1699, lng: 24.9384 },
        { name: 'Espoo', country: 'FI', population: 297132, lat: 60.2055, lng: 24.6559 },
        { name: 'Tampere', country: 'FI', population: 244029, lat: 61.4991, lng: 23.7871 },
        { name: 'Reykjavik', country: 'IS', population: 131136, lat: 64.1466, lng: -21.9426 },
        
        // Europe - Eastern Europe
        { name: 'Moscow', country: 'RU', population: 12615279, lat: 55.7558, lng: 37.6176 },
        { name: 'Saint Petersburg', country: 'RU', population: 5383890, lat: 59.9311, lng: 30.3609 },
        { name: 'Novosibirsk', country: 'RU', population: 1633595, lat: 55.0084, lng: 82.9357 },
        { name: 'Yekaterinburg', country: 'RU', population: 1493749, lat: 56.8431, lng: 60.6454 },
        { name: 'Kazan', country: 'RU', population: 1257391, lat: 55.8304, lng: 49.0661 },
        { name: 'Nizhny Novgorod', country: 'RU', population: 1252236, lat: 56.2965, lng: 43.9361 },
        { name: 'Chelyabinsk', country: 'RU', population: 1196680, lat: 55.1644, lng: 61.4368 },
        { name: 'Samara', country: 'RU', population: 1156644, lat: 53.2001, lng: 50.1500 },
        { name: 'Warsaw', country: 'PL', population: 1790658, lat: 52.2297, lng: 21.0122 },
        { name: 'Kraków', country: 'PL', population: 779115, lat: 50.0647, lng: 19.9450 },
        { name: 'Łódź', country: 'PL', population: 679941, lat: 51.7592, lng: 19.4560 },
        { name: 'Wrocław', country: 'PL', population: 643782, lat: 51.1079, lng: 17.0385 },
        { name: 'Poznań', country: 'PL', population: 538633, lat: 52.4064, lng: 16.9252 },
        { name: 'Prague', country: 'CZ', population: 1335084, lat: 50.0755, lng: 14.4378 },
        { name: 'Brno', country: 'CZ', population: 382405, lat: 49.1951, lng: 16.6068 },
        { name: 'Ostrava', country: 'CZ', population: 284982, lat: 49.8209, lng: 18.2625 },
        { name: 'Budapest', country: 'HU', population: 1752286, lat: 47.4979, lng: 19.0402 },
        { name: 'Debrecen', country: 'HU', population: 201981, lat: 47.5316, lng: 21.6273 },
        { name: 'Bucharest', country: 'RO', population: 1883425, lat: 44.4268, lng: 26.1025 },
        { name: 'Cluj-Napoca', country: 'RO', population: 286598, lat: 46.7712, lng: 23.6236 },
        { name: 'Sofia', country: 'BG', population: 1396551, lat: 42.6977, lng: 23.3219 },
        { name: 'Plovdiv', country: 'BG', population: 346893, lat: 42.1354, lng: 24.7453 },
        { name: 'Belgrade', country: 'RS', population: 1397939, lat: 44.7866, lng: 20.4489 },
        { name: 'Novi Sad', country: 'RS', population: 277522, lat: 45.2671, lng: 19.8335 },
        { name: 'Zagreb', country: 'HR', population: 688163, lat: 45.8150, lng: 15.9819 },
        { name: 'Split', country: 'HR', population: 161312, lat: 43.5081, lng: 16.4402 },
        { name: 'Ljubljana', country: 'SI', population: 295504, lat: 46.0569, lng: 14.5058 },
        { name: 'Bratislava', country: 'SK', population: 432864, lat: 48.1486, lng: 17.1077 },
        { name: 'Kiev', country: 'UA', population: 2952301, lat: 50.4501, lng: 30.5234 },
        { name: 'Kharkiv', country: 'UA', population: 1421125, lat: 49.9935, lng: 36.2304 },
        { name: 'Dnipro', country: 'UA', population: 980948, lat: 48.4647, lng: 35.0462 },
        { name: 'Minsk', country: 'BY', population: 2009786, lat: 53.9006, lng: 27.5590 },
        { name: 'Vilnius', country: 'LT', population: 588412, lat: 54.6872, lng: 25.2797 },
        { name: 'Riga', country: 'LV', population: 632614, lat: 56.9496, lng: 24.1052 },
        { name: 'Tallinn', country: 'EE', population: 437619, lat: 59.4370, lng: 24.7536 },
        
        // Asia - East Asia
        { name: 'Tokyo', country: 'JP', population: 37833000, lat: 35.6762, lng: 139.6503 },
        { name: 'Osaka', country: 'JP', population: 19222665, lat: 34.6937, lng: 135.5023 },
        { name: 'Nagoya', country: 'JP', population: 2327557, lat: 35.1815, lng: 136.9066 },
        { name: 'Sapporo', country: 'JP', population: 1973395, lat: 43.0642, lng: 141.3469 },
        { name: 'Fukuoka', country: 'JP', population: 1612392, lat: 33.5904, lng: 130.4017 },
        { name: 'Yokohama', country: 'JP', population: 3777491, lat: 35.4437, lng: 139.6380 },
        { name: 'Kobe', country: 'JP', population: 1522944, lat: 34.6901, lng: 135.1956 },
        { name: 'Kyoto', country: 'JP', population: 1474570, lat: 35.0116, lng: 135.7681 },
        { name: 'Seoul', country: 'KR', population: 25674000, lat: 37.5665, lng: 126.9780 },
        { name: 'Busan', country: 'KR', population: 3448737, lat: 35.1796, lng: 129.0756 },
        { name: 'Incheon', country: 'KR', population: 2954955, lat: 37.4563, lng: 126.7052 },
        { name: 'Daegu', country: 'KR', population: 2466052, lat: 35.8714, lng: 128.6014 },
        { name: 'Daejeon', country: 'KR', population: 1538394, lat: 36.3504, lng: 127.3845 },
        { name: 'Gwangju', country: 'KR', population: 1469214, lat: 35.1595, lng: 126.8526 },
        { name: 'Beijing', country: 'CN', population: 21516000, lat: 39.9042, lng: 116.4074 },
        { name: 'Shanghai', country: 'CN', population: 27058000, lat: 31.2304, lng: 121.4737 },
        { name: 'Guangzhou', country: 'CN', population: 18676605, lat: 23.1291, lng: 113.2644 },
        { name: 'Shenzhen', country: 'CN', population: 17560061, lat: 22.5431, lng: 114.0579 },
        { name: 'Tianjin', country: 'CN', population: 15621906, lat: 39.3434, lng: 117.3616 },
        { name: 'Chongqing', country: 'CN', population: 15872179, lat: 29.4316, lng: 106.9123 },
        { name: 'Chengdu', country: 'CN', population: 16045577, lat: 30.5728, lng: 104.0668 },
        { name: 'Nanjing', country: 'CN', population: 9314685, lat: 32.0603, lng: 118.7969 },
        { name: 'Wuhan', country: 'CN', population: 12508000, lat: 30.5928, lng: 114.3055 },
        { name: 'Xi\'an', country: 'CN', population: 12328000, lat: 34.3416, lng: 108.9398 },
        { name: 'Hangzhou', country: 'CN', population: 11936010, lat: 30.2741, lng: 120.1551 },
        { name: 'Qingdao', country: 'CN', population: 9046200, lat: 36.0986, lng: 120.3719 },
        { name: 'Zhengzhou', country: 'CN', population: 7617000, lat: 34.7466, lng: 113.6254 },
        { name: 'Taipei', country: 'TW', population: 2646204, lat: 25.0330, lng: 121.5654 },
        { name: 'Kaohsiung', country: 'TW', population: 2765107, lat: 22.6273, lng: 120.3014 },
        { name: 'Taichung', country: 'TW', population: 2820787, lat: 24.1477, lng: 120.6736 },
        { name: 'Hong Kong', country: 'HK', population: 7413000, lat: 22.3193, lng: 114.1694 },
        { name: 'Macau', country: 'MO', population: 682800, lat: 22.1987, lng: 113.5439 },
        
        // Asia - Southeast Asia
        { name: 'Jakarta', country: 'ID', population: 10770487, lat: -6.2088, lng: 106.8456 },
        { name: 'Surabaya', country: 'ID', population: 2874699, lat: -7.2575, lng: 112.7521 },
        { name: 'Bandung', country: 'ID', population: 2444160, lat: -6.9175, lng: 107.6191 },
        { name: 'Medan', country: 'ID', population: 2435252, lat: 3.5952, lng: 98.6722 },
        { name: 'Bekasi', country: 'ID', population: 2543676, lat: -6.2383, lng: 106.9756 },
        { name: 'Tangerang', country: 'ID', population: 2139891, lat: -6.1783, lng: 106.6319 },
        { name: 'Manila', country: 'PH', population: 13923452, lat: 14.5995, lng: 120.9842 },
        { name: 'Quezon City', country: 'PH', population: 2761720, lat: 14.6760, lng: 121.0437 },
        { name: 'Caloocan', country: 'PH', population: 1661584, lat: 14.6479, lng: 120.9634 },
        { name: 'Davao', country: 'PH', population: 1776949, lat: 7.1907, lng: 125.4553 },
        { name: 'Cebu City', country: 'PH', population: 964169, lat: 10.3157, lng: 123.8854 },
        { name: 'Bangkok', country: 'TH', population: 10156000, lat: 13.7563, lng: 100.5018 },
        { name: 'Nonthaburi', country: 'TH', population: 254375, lat: 13.8621, lng: 100.5144 },
        { name: 'Nakhon Ratchasima', country: 'TH', population: 174332, lat: 14.9799, lng: 102.0977 },
        { name: 'Chiang Mai', country: 'TH', population: 127240, lat: 18.7061, lng: 98.9817 },
        { name: 'Hat Yai', country: 'TH', population: 157678, lat: 7.0061, lng: 100.4686 },
        { name: 'Ho Chi Minh City', country: 'VN', population: 9077158, lat: 10.8231, lng: 106.6297 },
        { name: 'Hanoi', country: 'VN', population: 8246600, lat: 21.0285, lng: 105.8542 },
        { name: 'Haiphong', country: 'VN', population: 2028514, lat: 20.8449, lng: 106.6881 },
        { name: 'Da Nang', country: 'VN', population: 1134310, lat: 16.0471, lng: 108.2068 },
        { name: 'Bien Hoa', country: 'VN', population: 1104495, lat: 10.9452, lng: 106.8234 },
        { name: 'Singapore', country: 'SG', population: 5453600, lat: 1.3521, lng: 103.8198 },
        { name: 'Kuala Lumpur', country: 'MY', population: 1768000, lat: 3.1390, lng: 101.6869 },
        { name: 'George Town', country: 'MY', population: 708127, lat: 5.4164, lng: 100.3327 },
        { name: 'Ipoh', country: 'MY', population: 657892, lat: 4.5975, lng: 101.0901 },
        { name: 'Johor Bahru', country: 'MY', population: 497097, lat: 1.4927, lng: 103.7414 },
        { name: 'Yangon', country: 'MM', population: 5157000, lat: 16.8661, lng: 96.1951 },
        { name: 'Mandalay', country: 'MM', population: 1225546, lat: 21.9588, lng: 96.0891 },
        { name: 'Naypyidaw', country: 'MM', population: 924608, lat: 19.7633, lng: 96.0785 },
        { name: 'Phnom Penh', country: 'KH', population: 1573544, lat: 11.5564, lng: 104.9282 },
        { name: 'Vientiane', country: 'LA', population: 948477, lat: 17.9757, lng: 102.6331 },
        { name: 'Bandar Seri Begawan', country: 'BN', population: 100700, lat: 4.9031, lng: 114.9398 },
        
        // Asia - South Asia
        { name: 'Mumbai', country: 'IN', population: 21673000, lat: 19.0760, lng: 72.8777 },
        { name: 'Delhi', country: 'IN', population: 32226000, lat: 28.7041, lng: 77.1025 },
        { name: 'Bangalore', country: 'IN', population: 13193035, lat: 12.9716, lng: 77.5946 },
        { name: 'Hyderabad', country: 'IN', population: 10004144, lat: 17.3850, lng: 78.4867 },
        { name: 'Chennai', country: 'IN', population: 11324000, lat: 13.0827, lng: 80.2707 },
        { name: 'Kolkata', country: 'IN', population: 15133888, lat: 22.5726, lng: 88.3639 },
        { name: 'Pune', country: 'IN', population: 7764000, lat: 18.5204, lng: 73.8567 },
        { name: 'Ahmedabad', country: 'IN', population: 8253000, lat: 23.0225, lng: 72.5714 },
        { name: 'Surat', country: 'IN', population: 5807000, lat: 21.1702, lng: 72.8311 },
        { name: 'Jaipur', country: 'IN', population: 3592000, lat: 26.9124, lng: 75.7873 },
        { name: 'Lucknow', country: 'IN', population: 3382000, lat: 26.8467, lng: 80.9462 },
        { name: 'Kanpur', country: 'IN', population: 3162000, lat: 26.4499, lng: 80.3319 },
        { name: 'Nagpur', country: 'IN', population: 2653000, lat: 21.1458, lng: 79.0882 },
        { name: 'Indore', country: 'IN', population: 2302000, lat: 22.7196, lng: 75.8577 },
        { name: 'Thane', country: 'IN', population: 2290000, lat: 19.2183, lng: 72.9781 },
        { name: 'Bhopal', country: 'IN', population: 2371061, lat: 23.2599, lng: 77.4126 },
        { name: 'Visakhapatnam', country: 'IN', population: 2176000, lat: 17.6868, lng: 83.2185 },
        { name: 'Patna', country: 'IN', population: 2049156, lat: 25.5941, lng: 85.1376 },
        { name: 'Vadodara', country: 'IN', population: 1817000, lat: 22.3072, lng: 73.1812 },
        { name: 'Ghaziabad', country: 'IN', population: 1729000, lat: 28.6692, lng: 77.4538 },
        { name: 'Ludhiana', country: 'IN', population: 1618000, lat: 30.9010, lng: 75.8573 },
        { name: 'Agra', country: 'IN', population: 1585704, lat: 27.1767, lng: 78.0081 },
        { name: 'Karachi', country: 'PK', population: 16459472, lat: 24.8607, lng: 67.0011 },
        { name: 'Lahore', country: 'PK', population: 13541000, lat: 31.5204, lng: 74.3587 },
        { name: 'Faisalabad', country: 'PK', population: 3204726, lat: 31.4504, lng: 73.1350 },
        { name: 'Rawalpindi', country: 'PK', population: 2098231, lat: 33.5651, lng: 73.0169 },
        { name: 'Gujranwala', country: 'PK', population: 2027001, lat: 32.1877, lng: 74.1945 },
        { name: 'Peshawar', country: 'PK', population: 1970042, lat: 34.0151, lng: 71.5249 },
        { name: 'Multan', country: 'PK', population: 1871843, lat: 30.1575, lng: 71.5249 },
        { name: 'Islamabad', country: 'PK', population: 1014825, lat: 33.7294, lng: 73.0931 },
        { name: 'Quetta', country: 'PK', population: 1001205, lat: 30.1798, lng: 66.9750 },
        { name: 'Dhaka', country: 'BD', population: 9540000, lat: 23.8103, lng: 90.4125 },
        { name: 'Chittagong', country: 'BD', population: 2592439, lat: 22.3569, lng: 91.7832 },
        { name: 'Khulna', country: 'BD', population: 1022000, lat: 22.8456, lng: 89.5403 },
        { name: 'Rajshahi', country: 'BD', population: 449756, lat: 24.3745, lng: 88.6042 },
        { name: 'Sylhet', country: 'BD', population: 526412, lat: 24.8949, lng: 91.8687 },
        { name: 'Colombo', country: 'LK', population: 752993, lat: 6.9271, lng: 79.8612 },
        { name: 'Dehiwala-Mount Lavinia', country: 'LK', population: 245974, lat: 6.8344, lng: 79.8428 },
        { name: 'Moratuwa', country: 'LK', population: 207755, lat: 6.7762, lng: 79.8815 },
        { name: 'Kathmandu', country: 'NP', population: 1442271, lat: 27.7172, lng: 85.3240 },
        { name: 'Pokhara', country: 'NP', population: 599504, lat: 28.2096, lng: 83.9856 },
        { name: 'Thimphu', country: 'BT', population: 114551, lat: 27.4728, lng: 89.6393 },
        { name: 'Malé', country: 'MV', population: 252768, lat: 4.1755, lng: 73.5093 },
        
        // Asia - Central & Western Asia
        { name: 'Istanbul', country: 'TR', population: 15519267, lat: 41.0082, lng: 28.9784 },
        { name: 'Ankara', country: 'TR', population: 5503985, lat: 39.9334, lng: 32.8597 },
        { name: 'Izmir', country: 'TR', population: 4394694, lat: 38.4192, lng: 27.1287 },
        { name: 'Bursa', country: 'TR', population: 3147818, lat: 40.1826, lng: 29.0665 },
        { name: 'Adana', country: 'TR', population: 2274106, lat: 37.0000, lng: 35.3213 },
        { name: 'Gaziantep', country: 'TR', population: 2069364, lat: 37.0662, lng: 37.3833 },
        { name: 'Konya', country: 'TR', population: 2232374, lat: 37.8713, lng: 32.4846 },
        { name: 'Tehran', country: 'IR', population: 9259009, lat: 35.6892, lng: 51.3890 },
        { name: 'Mashhad', country: 'IR', population: 3312090, lat: 36.2605, lng: 59.6168 },
        { name: 'Isfahan', country: 'IR', population: 2220000, lat: 32.6546, lng: 51.6680 },
        { name: 'Karaj', country: 'IR', population: 1973837, lat: 35.8327, lng: 50.9916 },
        { name: 'Shiraz', country: 'IR', population: 1869001, lat: 29.5918, lng: 52.5837 },
        { name: 'Tabriz', country: 'IR', population: 1733033, lat: 38.0800, lng: 46.2919 },
        { name: 'Qom', country: 'IR', population: 1292283, lat: 34.6401, lng: 50.8764 },
        { name: 'Baghdad', country: 'IQ', population: 7711305, lat: 33.3152, lng: 44.3661 },
        { name: 'Basra', country: 'IQ', population: 2600000, lat: 30.5260, lng: 47.7804 },
        { name: 'Mosul', country: 'IQ', population: 1683000, lat: 36.3350, lng: 43.1189 },
        { name: 'Erbil', country: 'IQ', population: 1612700, lat: 36.1911, lng: 44.0092 },
        { name: 'Sulaymaniyah', country: 'IQ', population: 1784853, lat: 35.5650, lng: 45.4329 },
        { name: 'Riyadh', country: 'SA', population: 7676654, lat: 24.7136, lng: 46.6753 },
        { name: 'Jeddah', country: 'SA', population: 4697000, lat: 21.4858, lng: 39.1925 },
        { name: 'Mecca', country: 'SA', population: 2385509, lat: 21.3891, lng: 39.8579 },
        { name: 'Medina', country: 'SA', population: 1488782, lat: 24.5247, lng: 39.5692 },
        { name: 'Dammam', country: 'SA', population: 1252523, lat: 26.3927, lng: 49.9777 },
        { name: 'Dubai', country: 'AE', population: 3331420, lat: 25.2048, lng: 55.2708 },
        { name: 'Abu Dhabi', country: 'AE', population: 1483000, lat: 24.4539, lng: 54.3773 },
        { name: 'Sharjah', country: 'AE', population: 1684649, lat: 25.3463, lng: 55.4209 },
        { name: 'Al Ain', country: 'AE', population: 766936, lat: 24.2075, lng: 55.7447 },
        { name: 'Kuwait City', country: 'KW', population: 4270563, lat: 29.3759, lng: 47.9774 },
        { name: 'Doha', country: 'QA', population: 2382000, lat: 25.2854, lng: 51.5310 },
        { name: 'Manama', country: 'BH', population: 650604, lat: 26.2285, lng: 50.5860 },
        { name: 'Muscat', country: 'OM', population: 1560330, lat: 23.5859, lng: 58.4059 },
        { name: 'Sanaa', country: 'YE', population: 2961000, lat: 15.3694, lng: 44.1910 },
        { name: 'Aden', country: 'YE', population: 1080000, lat: 12.7797, lng: 45.0162 },
        { name: 'Damascus', country: 'SY', population: 2503000, lat: 33.5138, lng: 36.2765 },
        { name: 'Aleppo', country: 'SY', population: 2132000, lat: 36.2021, lng: 37.1343 },
        { name: 'Beirut', country: 'LB', population: 2407000, lat: 33.8938, lng: 35.5018 },
        { name: 'Tripoli', country: 'LB', population: 229398, lat: 34.4372, lng: 35.8428 },
        { name: 'Amman', country: 'JO', population: 2065000, lat: 31.9539, lng: 35.9106 },
        { name: 'Zarqa', country: 'JO', population: 635160, lat: 32.0728, lng: 36.0876 },
        { name: 'Jerusalem', country: 'IL', population: 936425, lat: 31.7683, lng: 35.2137 },
        { name: 'Tel Aviv', country: 'IL', population: 460613, lat: 32.0853, lng: 34.7818 },
        { name: 'Haifa', country: 'IL', population: 285316, lat: 32.7940, lng: 34.9896 },
        { name: 'Tashkent', country: 'UZ', population: 2906000, lat: 41.2995, lng: 69.2401 },
        { name: 'Samarkand', country: 'UZ', population: 509000, lat: 39.6542, lng: 66.9597 },
        { name: 'Namangan', country: 'UZ', population: 475700, lat: 40.9983, lng: 71.6726 },
        { name: 'Almaty', country: 'KZ', population: 1916822, lat: 43.2220, lng: 76.8512 },
        { name: 'Nur-Sultan', country: 'KZ', population: 1184469, lat: 51.1694, lng: 71.4491 },
        { name: 'Shymkent', country: 'KZ', population: 1002895, lat: 42.3000, lng: 69.5992 },
        { name: 'Bishkek', country: 'KG', population: 1074075, lat: 42.8746, lng: 74.5698 },
        { name: 'Dushanbe', country: 'TJ', population: 863400, lat: 38.5598, lng: 68.7870 },
        { name: 'Ashgabat', country: 'TM', population: 1031992, lat: 37.9601, lng: 58.3261 },
        { name: 'Baku', country: 'AZ', population: 2313077, lat: 40.4093, lng: 49.8671 },
        { name: 'Ganja', country: 'AZ', population: 335600, lat: 40.6833, lng: 46.3611 },
        { name: 'Yerevan', country: 'AM', population: 1086677, lat: 40.1792, lng: 44.4991 },
        { name: 'Gyumri', country: 'AM', population: 120138, lat: 40.7942, lng: 43.8503 },
        { name: 'Tbilisi', country: 'GE', population: 1049498, lat: 41.7151, lng: 44.8271 },
        { name: 'Batumi', country: 'GE', population: 204156, lat: 41.6168, lng: 41.6367 },
        
        // Africa
        { name: 'Cairo', country: 'EG', population: 20484965, lat: 30.0444, lng: 31.2357 },
        { name: 'Alexandria', country: 'EG', population: 5200000, lat: 31.2001, lng: 29.9187 },
        { name: 'Giza', country: 'EG', population: 9200000, lat: 30.0131, lng: 31.2089 },
        { name: 'Shubra El Kheima', country: 'EG', population: 1165914, lat: 30.1284, lng: 31.2441 },
        { name: 'Port Said', country: 'EG', population: 749371, lat: 31.2653, lng: 32.3019 },
        { name: 'Suez', country: 'EG', population: 728180, lat: 29.9737, lng: 32.5263 },
        { name: 'Lagos', country: 'NG', population: 15946000, lat: 6.5244, lng: 3.3792 },
        { name: 'Kano', country: 'NG', population: 4103000, lat: 12.0022, lng: 8.5920 },
        { name: 'Ibadan', country: 'NG', population: 3649000, lat: 7.3775, lng: 3.9470 },
        { name: 'Abuja', country: 'NG', population: 3464000, lat: 9.0579, lng: 7.4951 },
        { name: 'Port Harcourt', country: 'NG', population: 3020000, lat: 4.8156, lng: 7.0498 },
        { name: 'Benin City', country: 'NG', population: 1782000, lat: 6.3350, lng: 5.6037 },
        { name: 'Maiduguri', country: 'NG', population: 1952000, lat: 11.8311, lng: 13.1510 },
        { name: 'Zaria', country: 'NG', population: 1018000, lat: 11.0804, lng: 7.7056 },
        { name: 'Aba', country: 'NG', population: 1148000, lat: 5.1066, lng: 7.3667 },
        { name: 'Jos', country: 'NG', population: 1000000, lat: 9.9285, lng: 8.8921 },
        { name: 'Johannesburg', country: 'ZA', population: 5635127, lat: -26.2041, lng: 28.0473 },
        { name: 'Cape Town', country: 'ZA', population: 4617560, lat: -33.9249, lng: 18.4241 },
        { name: 'Durban', country: 'ZA', population: 3442361, lat: -29.8587, lng: 31.0218 },
        { name: 'Pretoria', country: 'ZA', population: 2345908, lat: -25.7479, lng: 28.2293 },
        { name: 'Port Elizabeth', country: 'ZA', population: 1263051, lat: -33.9180, lng: 25.5701 },
        { name: 'Bloemfontein', country: 'ZA', population: 747431, lat: -29.0852, lng: 26.1596 },
        { name: 'East London', country: 'ZA', population: 695047, lat: -33.0153, lng: 27.9116 },
        { name: 'Pietermaritzburg', country: 'ZA', population: 750845, lat: -29.6197, lng: 30.3925 },
        { name: 'Casablanca', country: 'MA', population: 3359818, lat: 33.5731, lng: -7.5898 },
        { name: 'Rabat', country: 'MA', population: 577827, lat: 34.0209, lng: -6.8416 },
        { name: 'Marrakech', country: 'MA', population: 928850, lat: 31.6295, lng: -7.9811 },
        { name: 'Fez', country: 'MA', population: 1112072, lat: 34.0181, lng: -5.0078 },
        { name: 'Tangier', country: 'MA', population: 1065601, lat: 35.7595, lng: -5.8340 },
        { name: 'Algiers', country: 'DZ', population: 2988145, lat: 36.7538, lng: 3.0588 },
        { name: 'Oran', country: 'DZ', population: 803329, lat: 35.6969, lng: -0.6331 },
        { name: 'Constantine', country: 'DZ', population: 450097, lat: 36.3650, lng: 6.6147 },
        { name: 'Annaba', country: 'DZ', population: 257359, lat: 36.9000, lng: 7.7667 },
        { name: 'Tunis', country: 'TN', population: 2412500, lat: 36.8065, lng: 10.1815 },
        { name: 'Sfax', country: 'TN', population: 330440, lat: 34.7406, lng: 10.7603 },
        { name: 'Sousse', country: 'TN', population: 271428, lat: 35.8256, lng: 10.6411 },
        { name: 'Tripoli', country: 'LY', population: 1176000, lat: 32.8872, lng: 13.1913 },
        { name: 'Benghazi', country: 'LY', population: 631555, lat: 32.1167, lng: 20.0667 },
        { name: 'Misrata', country: 'LY', population: 550938, lat: 32.3744, lng: 15.0925 },
        { name: 'Khartoum', country: 'SD', population: 5274321, lat: 15.5007, lng: 32.5599 },
        { name: 'Omdurman', country: 'SD', population: 2395159, lat: 15.6445, lng: 32.4777 },
        { name: 'Port Sudan', country: 'SD', population: 489725, lat: 19.6350, lng: 37.2162 },
        { name: 'Kassala', country: 'SD', population: 419030, lat: 15.4500, lng: 36.4000 },
        { name: 'Addis Ababa', country: 'ET', population: 4794000, lat: 9.1450, lng: 38.7451 },
        { name: 'Dire Dawa', country: 'ET', population: 440000, lat: 9.5950, lng: 41.8660 },
        { name: 'Mek\'ele', country: 'ET', population: 340859, lat: 13.4967, lng: 39.4753 },
        { name: 'Nairobi', country: 'KE', population: 4922000, lat: -1.2921, lng: 36.8219 },
        { name: 'Mombasa', country: 'KE', population: 1208333, lat: -4.0435, lng: 39.6682 },
        { name: 'Nakuru', country: 'KE', population: 570674, lat: -0.3031, lng: 36.0800 },
        { name: 'Kampala', country: 'UG', population: 1650800, lat: 0.3476, lng: 32.5825 },
        { name: 'Dar es Salaam', country: 'TZ', population: 6701650, lat: -6.7924, lng: 39.2083 },
        { name: 'Mwanza', country: 'TZ', population: 706453, lat: -2.5164, lng: 32.9175 },
        { name: 'Arusha', country: 'TZ', population: 416442, lat: -3.3869, lng: 36.6830 },
        { name: 'Kigali', country: 'RW', population: 1132686, lat: -1.9441, lng: 30.0619 },
        { name: 'Lusaka', country: 'ZM', population: 2774000, lat: -15.3875, lng: 28.3228 },
        { name: 'Kitwe', country: 'ZM', population: 517543, lat: -12.8024, lng: 28.2132 },
        { name: 'Ndola', country: 'ZM', population: 455194, lat: -12.9587, lng: 28.6366 },
        { name: 'Harare', country: 'ZW', population: 1542813, lat: -17.8252, lng: 31.0335 },
        { name: 'Bulawayo', country: 'ZW', population: 653337, lat: -20.1500, lng: 28.5833 },
        { name: 'Chitungwiza', country: 'ZW', population: 365000, lat: -18.0133, lng: 31.0689 },
        { name: 'Maputo', country: 'MZ', population: 1191613, lat: -25.9692, lng: 32.5732 },
        { name: 'Matola', country: 'MZ', population: 1032197, lat: -25.9622, lng: 32.4589 },
        { name: 'Beira', country: 'MZ', population: 592090, lat: -19.8433, lng: 34.8394 },
        { name: 'Nampula', country: 'MZ', population: 743125, lat: -15.1165, lng: 39.2666 },
        { name: 'Lilongwe', country: 'MW', population: 1030924, lat: -13.9626, lng: 33.7741 },
        { name: 'Blantyre', country: 'MW', population: 800264, lat: -15.7861, lng: 35.0058 },
        { name: 'Gaborone', country: 'BW', population: 246325, lat: -24.6282, lng: 25.9231 },
        { name: 'Windhoek', country: 'NA', population: 431000, lat: -22.5609, lng: 17.0658 },
        { name: 'Maseru', country: 'LS', population: 330790, lat: -29.3142, lng: 27.4869 },
        { name: 'Mbabane', country: 'SZ', population: 94874, lat: -26.3054, lng: 31.1367 },
        { name: 'Accra', country: 'GH', population: 2291352, lat: 5.6037, lng: -0.1870 },
        { name: 'Kumasi', country: 'GH', population: 3348000, lat: 6.6885, lng: -1.6244 },
        { name: 'Tamale', country: 'GH', population: 537986, lat: 9.4008, lng: -0.8393 },
        { name: 'Sekondi-Takoradi', country: 'GH', population: 445205, lat: 4.9344, lng: -1.7133 },
        { name: 'Abidjan', country: 'CI', population: 4707404, lat: 5.3364, lng: -4.0267 },
        { name: 'Bouaké', country: 'CI', population: 536719, lat: 7.6843, lng: -5.0295 },
        { name: 'Daloa', country: 'CI', population: 319427, lat: 6.8772, lng: -6.4503 },
        { name: 'Yamoussoukro', country: 'CI', population: 355573, lat: 6.8276, lng: -5.2893 },
        { name: 'Dakar', country: 'SN', population: 2476400, lat: 14.7167, lng: -17.4677 },
        { name: 'Touba', country: 'SN', population: 529176, lat: 14.8500, lng: -15.8833 },
        { name: 'Thiès', country: 'SN', population: 320000, lat: 14.7886, lng: -16.9260 },
        { name: 'Kaolack', country: 'SN', population: 233708, lat: 14.1500, lng: -16.0667 },
        
        // Oceania
        { name: 'Sydney', country: 'AU', population: 5312163, lat: -33.8688, lng: 151.2093 },
        { name: 'Melbourne', country: 'AU', population: 5159211, lat: -37.8136, lng: 144.9631 },
        { name: 'Brisbane', country: 'AU', population: 2560720, lat: -27.4705, lng: 153.0260 },
        { name: 'Perth', country: 'AU', population: 2192229, lat: -31.9505, lng: 115.8605 },
        { name: 'Adelaide', country: 'AU', population: 1359760, lat: -34.9285, lng: 138.6007 },
        { name: 'Gold Coast', country: 'AU', population: 679127, lat: -28.0167, lng: 153.4000 },
        { name: 'Newcastle', country: 'AU', population: 322278, lat: -32.9267, lng: 151.7789 },
        { name: 'Canberra', country: 'AU', population: 431380, lat: -35.2809, lng: 149.1300 },
        { name: 'Central Coast', country: 'AU', population: 333627, lat: -33.4269, lng: 151.3425 },
        { name: 'Wollongong', country: 'AU', population: 305691, lat: -34.4278, lng: 150.8931 },
        { name: 'Logan City', country: 'AU', population: 326615, lat: -27.6394, lng: 153.1095 },
        { name: 'Geelong', country: 'AU', population: 253269, lat: -38.1499, lng: 144.3617 },
        { name: 'Hobart', country: 'AU', population: 238834, lat: -42.8821, lng: 147.3272 },
        { name: 'Townsville', country: 'AU', population: 179011, lat: -19.2564, lng: 146.8183 },
        { name: 'Cairns', country: 'AU', population: 153075, lat: -16.9186, lng: 145.7781 },
        { name: 'Toowoomba', country: 'AU', population: 138062, lat: -27.5598, lng: 151.9507 },
        { name: 'Darwin', country: 'AU', population: 148564, lat: -12.4634, lng: 130.8456 },
        { name: 'Auckland', country: 'NZ', population: 1470100, lat: -36.8485, lng: 174.7633 },
        { name: 'Wellington', country: 'NZ', population: 215200, lat: -41.2865, lng: 174.7762 },
        { name: 'Christchurch', country: 'NZ', population: 381500, lat: -43.5321, lng: 172.6362 },
        { name: 'Hamilton', country: 'NZ', population: 169800, lat: -37.7870, lng: 175.2793 },
        { name: 'Tauranga', country: 'NZ', population: 151300, lat: -37.6878, lng: 176.1651 },
        { name: 'Napier-Hastings', country: 'NZ', population: 140100, lat: -39.4928, lng: 176.9120 },
        { name: 'Dunedin', country: 'NZ', population: 130500, lat: -45.8788, lng: 170.5028 },
        { name: 'Palmerston North', country: 'NZ', population: 90100, lat: -40.3523, lng: 175.6082 },
        { name: 'New Plymouth', country: 'NZ', population: 59300, lat: -39.0579, lng: 174.0806 },
        { name: 'Rotorua', country: 'NZ', population: 58800, lat: -38.1368, lng: 176.2497 },
        { name: 'Whangarei', country: 'NZ', population: 56200, lat: -35.7275, lng: 174.3166 },
        { name: 'Nelson', country: 'NZ', population: 51800, lat: -41.2706, lng: 173.2840 },
        { name: 'Suva', country: 'FJ', population: 185913, lat: -18.1248, lng: 178.4501 },
        { name: 'Lautoka', country: 'FJ', population: 71573, lat: -17.6170, lng: 177.4500 },
        { name: 'Nadi', country: 'FJ', population: 71048, lat: -17.7734, lng: 177.4462 },
        { name: 'Port Moresby', country: 'PG', population: 364145, lat: -9.4438, lng: 147.1803 },
        { name: 'Lae', country: 'PG', population: 148934, lat: -6.7184, lng: 146.9841 },
        { name: 'Mount Hagen', country: 'PG', population: 46250, lat: -5.8520, lng: 144.2305 },
        { name: 'Madang', country: 'PG', population: 35971, lat: -5.2240, lng: 145.7969 },
        { name: 'Wewak', country: 'PG', population: 25143, lat: -3.5550, lng: 143.6280 },
        { name: 'Vanimo', country: 'PG', population: 11204, lat: -2.6742, lng: 141.3028 },
        { name: 'Nouméa', country: 'NC', population: 179509, lat: -22.2710, lng: 166.4416 },
        { name: 'Apia', country: 'WS', population: 37708, lat: -13.8506, lng: -171.7513 },
        { name: 'Nuku\'alofa', country: 'TO', population: 24229, lat: -21.1385, lng: -175.2206 },
        { name: 'Port Vila', country: 'VU', population: 51437, lat: -17.7334, lng: 168.3273 },
        { name: 'Honiara', country: 'SB', population: 84520, lat: -9.4280, lng: 159.9495 },
        { name: 'Tarawa', country: 'KI', population: 56284, lat: 1.4518, lng: 172.9717 },
        { name: 'Majuro', country: 'MH', population: 27797, lat: 7.0897, lng: 171.3803 },
        { name: 'Palikir', country: 'FM', population: 6647, lat: 6.9248, lng: 158.1611 },
        { name: 'Ngerulmud', country: 'PW', population: 391, lat: 7.5006, lng: 134.6242 },
      ]
      
      // Add countries
      countries.forEach(country => {
        const searchTerms = [
          country.name.toLowerCase(),
          country.isoCode.toLowerCase(),
          ...(country.name.toLowerCase().includes('states') ? ['usa', 'america'] : []),
          ...(country.name.toLowerCase().includes('kingdom') ? ['uk', 'britain', 'england'] : []),
        ]
        
        this.locations.push({
          id: `country-${country.isoCode}`,
          name: country.name,
          country: country.name,
          countryCode: country.isoCode,
          type: 'country',
          searchTerms,
          displayName: country.name
        })
      })
      
      // Process curated major cities
      console.log('Processing curated cities...')
      majorCitiesData.forEach((city) => {
        const countryInfo = countries.find(c => c.isoCode === city.country)
        if (!countryInfo) return
        
        const searchTerms = [
          city.name.toLowerCase(),
          countryInfo.name.toLowerCase(),
          countryInfo.isoCode.toLowerCase(),
          // Add common city nicknames and variations
          ...(city.name.toLowerCase() === 'new york' ? ['nyc', 'manhattan', 'brooklyn'] : []),
          ...(city.name.toLowerCase() === 'los angeles' ? ['la', 'hollywood'] : []),
          ...(city.name.toLowerCase() === 'san francisco' ? ['sf', 'bay area'] : []),
          ...(city.name.toLowerCase() === 'las vegas' ? ['vegas'] : []),
          ...(city.name.toLowerCase() === 'washington' ? ['dc', 'capital'] : []),
          ...(city.name.toLowerCase() === 'rio de janeiro' ? ['rio'] : []),
          ...(city.name.toLowerCase() === 'santo domingo' ? ['dominican republic', 'dr', 'dominican'] : []),
          ...(city.name.toLowerCase() === 'santiago de los caballeros' ? ['santiago', 'dominican republic'] : []),
        ]
        
        this.locations.push({
          id: `city-${city.name}-${city.country}`,
          name: city.name,
          country: countryInfo.name,
          countryCode: city.country,
          type: 'city',
          population: city.population,
          searchTerms,
          displayName: `${city.name}, ${countryInfo.name}`,
          latitude: city.lat,
          longitude: city.lng
        })
      })
      console.log(`Processed ${majorCitiesData.length} curated cities`)
      
      // Add major states/provinces for large countries
      const majorCountries = ['US', 'CA', 'AU', 'IN', 'BR', 'DE', 'ES', 'IT', 'FR', 'GB']
      majorCountries.forEach(countryCode => {
        const states = State.getStatesOfCountry(countryCode)
        const country = countries.find(c => c.isoCode === countryCode)
        if (!country) return
        
        states.forEach(state => {
          if (state.name && state.name.length > 0) {
            const searchTerms = [
              state.name.toLowerCase(),
              state.isoCode.toLowerCase(),
              country.name.toLowerCase(),
            ]
            
            this.locations.push({
              id: `state-${state.isoCode}`,
              name: state.name,
              country: country.name,
              countryCode: countryCode,
              state: state.name,
              stateCode: state.isoCode,
              type: 'state',
              searchTerms,
              displayName: `${state.name}, ${country.name}`
            })
          }
        })
      })
      
      // Add some famous landmarks manually
      const landmarks = [
        { name: 'Times Square', city: 'New York', country: 'United States', terms: ['times square', 'manhattan', 'nyc'] },
        { name: 'Central Park', city: 'New York', country: 'United States', terms: ['central park', 'manhattan', 'nyc'] },
        { name: 'Golden Gate Bridge', city: 'San Francisco', country: 'United States', terms: ['golden gate', 'bridge', 'sf'] },
        { name: 'Statue of Liberty', city: 'New York', country: 'United States', terms: ['statue of liberty', 'liberty island'] },
        { name: 'Eiffel Tower', city: 'Paris', country: 'France', terms: ['eiffel tower', 'paris', 'france'] },
        { name: 'Big Ben', city: 'London', country: 'United Kingdom', terms: ['big ben', 'london', 'parliament'] },
        { name: 'Colosseum', city: 'Rome', country: 'Italy', terms: ['colosseum', 'rome', 'italy'] },
        { name: 'Machu Picchu', city: 'Cusco', country: 'Peru', terms: ['machu picchu', 'peru', 'inca'] },
        { name: 'Great Wall of China', city: 'Beijing', country: 'China', terms: ['great wall', 'china', 'wall'] },
        { name: 'Taj Mahal', city: 'Agra', country: 'India', terms: ['taj mahal', 'india', 'agra'] },
        { name: 'Sydney Opera House', city: 'Sydney', country: 'Australia', terms: ['opera house', 'sydney'] },
        { name: 'Christ the Redeemer', city: 'Rio de Janeiro', country: 'Brazil', terms: ['christ redeemer', 'rio'] },
        { name: 'Hollywood', city: 'Los Angeles', country: 'United States', terms: ['hollywood', 'la', 'movies'] },
        { name: 'Silicon Valley', city: 'San Jose', country: 'United States', terms: ['silicon valley', 'tech', 'california'] },
        { name: 'Wall Street', city: 'New York', country: 'United States', terms: ['wall street', 'financial', 'nyc'] },
      ]
      
      landmarks.forEach((landmark, index) => {
        this.locations.push({
          id: `landmark-${index}`,
          name: landmark.name,
          country: landmark.country,
          countryCode: countries.find(c => c.name === landmark.country)?.isoCode || '',
          type: 'city', // Treat landmarks as cities for simplicity
          searchTerms: landmark.terms,
          displayName: `${landmark.name}, ${landmark.country}`
        })
      })
      
      // Sort by population and importance
      this.locations.sort((a, b) => {
        // Countries first, then by population
        if (a.type === 'country' && b.type !== 'country') return -1
        if (b.type === 'country' && a.type !== 'country') return 1
        
        // Then by population
        const popA = a.population || 0
        const popB = b.population || 0
        return popB - popA
      })
      
      console.log(`Loaded ${this.locations.length} locations`)
      this.initialized = true
    } catch (error) {
      console.error('Failed to initialize location database:', error)
      this.initialized = true // Still mark as initialized to prevent retry loops
    }
  }

  search(query: string, limit: number = 10): LocationItem[] {
    if (!this.initialized || !query || query.length < 2) return []
    
    const searchQuery = query.toLowerCase().trim()
    const results: { item: LocationItem; score: number }[] = []
    
    this.locations.forEach(location => {
      let score = 0
      
      // Exact name match (highest priority)
      if (location.name.toLowerCase() === searchQuery) {
        score = 1000
      }
      // Name starts with query (high priority)
      else if (location.name.toLowerCase().startsWith(searchQuery)) {
        score = 500
      }
      // Name contains query
      else if (location.name.toLowerCase().includes(searchQuery)) {
        score = 300
      }
      // Search terms match
      else if (location.searchTerms.some(term => term.includes(searchQuery))) {
        score = 200
      }
      // Country match
      else if (location.country.toLowerCase().includes(searchQuery)) {
        score = 100
      }
      
      // Boost score based on type and population
      if (score > 0) {
        // Type bonuses
        if (location.type === 'country') score += 50
        else if (location.type === 'city') score += 30
        else if (location.type === 'state') score += 20
        
        // Population bonus for cities
        if (location.population) {
          score += Math.min(location.population / 100000, 50) // Max 50 point bonus
        }
        
        results.push({ item: location, score })
      }
    })
    
    // Sort by score and return top results
    return results
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(r => r.item)
  }

  getLocationById(id: string): LocationItem | undefined {
    return this.locations.find(loc => loc.id === id)
  }

  getAll(): LocationItem[] {
    return [...this.locations]
  }
}

// Create singleton instance
export const locationDatabase = new LocationDatabase()

// Initialize on first use instead of immediately

/**
 * Search locations with the database
 */
export async function searchLocations(query: string, limit: number = 10): Promise<LocationItem[]> {
  try {
    // Initialize on first use with timeout
    if (!locationDatabase.isInitialized) {
      console.log('Initializing location database for search...')
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Database initialization timeout')), 5000)
      )
      
      await Promise.race([
        locationDatabase.initialize(),
        timeoutPromise
      ])
    }
    return locationDatabase.search(query, limit)
  } catch (error) {
    console.error('Location search failed:', error)
    // Return empty results on error
    return []
  }
}

/**
 * Format location for display
 */
export function formatLocation(location: LocationItem): string {
  return location.displayName
}